import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createItem } from '../../../shared/api/requestItems.api'
import { lookupBrand } from '../../../shared/api/partCatalog.api'
import { ErrorMessage } from '../../../shared/ui/ErrorMessage'
import type { RequestItemPriority } from '../../../shared/api/types'

export function AddItemForm({ requestId }: { requestId: string }) {
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [partNumber, setPartNumber] = useState('')
  const [brand, setBrand] = useState('')
  const [quantity, setQuantity] = useState(1)
  const [unit, setUnit] = useState('pcs')
  const [priority, setPriority] = useState<RequestItemPriority>('MEDIUM')
  const [noteForProcurement, setNoteForProcurement] = useState('')

  const mutation = useMutation({
    mutationFn: () =>
      createItem(requestId, {
        partNumber: partNumber.trim().toUpperCase(),
        brand: brand.trim() || null,
        quantity,
        unit: unit || null,
        priority,
        noteForProcurement: noteForProcurement.trim() || null,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['requests', requestId] })
      setPartNumber('')
      setBrand('')
      setQuantity(1)
      setUnit('pcs')
      setPriority('MEDIUM')
      setNoteForProcurement('')
    },
  })

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="text-xs text-brand-600 hover:text-brand-700 font-medium"
      >
        + Добавить позицию
      </button>
    )
  }

  return (
    <tr className="bg-blue-50/40">
      <td colSpan={8} className="px-4 py-2">
        <div className="flex flex-wrap gap-2 items-center">
          <input
            autoFocus
            type="text"
            placeholder="Артикул *"
            value={partNumber}
            onChange={(e) => setPartNumber(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setOpen(false)
            }}
            onBlur={async () => {
              const pn = partNumber.trim()
              if (!pn || brand.trim()) return
              try {
                const found = await lookupBrand(pn)
                if (found) setBrand(found)
              } catch {
                // автозаполнение — best-effort, не прерываем работу
              }
            }}
            className="px-2 py-1 border border-gray-300 rounded text-xs font-mono w-40 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
          <input
            type="text"
            placeholder="Бренд"
            value={brand}
            onChange={(e) => setBrand(e.target.value)}
            className="px-2 py-1 border border-gray-300 rounded text-xs w-24 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
          <input
            type="number"
            min={1}
            value={quantity}
            onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
            className="px-2 py-1 border border-gray-300 rounded text-xs w-16 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
          <input
            type="text"
            placeholder="ед."
            value={unit}
            onChange={(e) => setUnit(e.target.value)}
            className="px-2 py-1 border border-gray-300 rounded text-xs w-12 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
          <select
            value={priority}
            onChange={(e) => setPriority(e.target.value as RequestItemPriority)}
            className="px-2 py-1 border border-gray-300 rounded text-xs bg-white focus:outline-none focus:ring-1 focus:ring-brand-500"
          >
            <option value="HIGH">🔴 Высокий</option>
            <option value="MEDIUM">🟡 Средний</option>
            <option value="LOW">⚪ Низкий</option>
          </select>
          <input
            type="text"
            placeholder="Заметка для закупки"
            value={noteForProcurement}
            onChange={(e) => setNoteForProcurement(e.target.value)}
            className="px-2 py-1 border border-gray-300 rounded text-xs flex-1 min-w-32 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
          <button
            onClick={() => mutation.mutate()}
            disabled={!partNumber.trim() || mutation.isPending}
            className="px-3 py-1 bg-brand-600 text-white text-xs rounded hover:bg-brand-700 disabled:opacity-50 transition-colors"
          >
            {mutation.isPending ? '...' : 'Добавить'}
          </button>
          <button
            onClick={() => setOpen(false)}
            className="px-2 py-1 text-gray-400 text-xs hover:text-gray-700"
          >
            Отмена
          </button>
        </div>
        {mutation.isError && (
          <div className="mt-1">
            <ErrorMessage error={mutation.error} />
          </div>
        )}
      </td>
    </tr>
  )
}
